import { useCustom } from '@refinedev/core';
import { Alert, Card, Col, Empty, Row, Spin, Statistic, Table, Typography } from 'antd';
import { Link } from 'react-router';
import { API_URL } from '../providers';
import { renderValue } from '../render';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Overview = Record<string, any>;

const listLink = ( resource: string, query = '' ) => `/${ resource }${ query ? `?${ query }` : '' }`;

/** What an operator checks first: is every binding running, is anything failing, do signals get out. */
export function Dashboard() {
	const { query, result } = useCustom<Overview>( { url: `${ API_URL }/overview`, method: 'get', queryOptions: { refetchInterval: 60_000 } } );
	if ( query.isLoading ) {
		return <Spin />;
	}
	const o = result.data;
	if ( ! o ) {
		return <Empty />;
	}
	const issues = Object.entries( o.open_issues as Record<string, number> );
	const runs = o.runs_24h as Record<string, number>;
	return (
		<Row gutter={ [ 16, 16 ] }>
			{ o.failing.length ? (
				<Col span={ 24 }>
					<Alert
						type="error"
						showIcon
						message={ `${ o.failing.length } binding(s) failing` }
						description={ o.failing.map( ( f: Overview ) => (
							<div key={ f.binding }>
								{ renderValue( 'binding', f.binding ) }: { renderValue( 'run', f.run ) } { f.error }
							</div>
						) ) }
					/>
				</Col>
			) : null }
			<Col xs={ 24 } md={ 6 }>
				<Card title={ <Link to={ listLink( 'bindings' ) }>Bindings</Link> }>
					<Statistic title="active" value={ o.bindings.total - o.bindings.paused } suffix={ `/ ${ o.bindings.total }` } />
					<Statistic title="overdue > 30 min" value={ o.bindings.overdue } valueStyle={ o.bindings.overdue ? { color: '#cf1322' } : {} } />
				</Card>
			</Col>
			<Col xs={ 24 } md={ 6 }>
				<Card title={ <Link to={ listLink( 'runs' ) }>Runs, 24 h</Link> }>
					<Statistic title="ok / partial" value={ ( runs.ok ?? 0 ) + ( runs.partial ?? 0 ) } />
					<Statistic title="failed" value={ runs.failed ?? 0 } valueStyle={ runs.failed ? { color: '#cf1322' } : {} } />
				</Card>
			</Col>
			<Col xs={ 24 } md={ 6 }>
				<Card title={ <Link to={ listLink( 'issues', 'filters[0][field]=open&filters[0][operator]=eq&filters[0][value]=true' ) }>Open issues</Link> }>
					{ issues.length ? issues.map( ( [ severity, count ] ) => <Statistic key={ severity } title={ severity } value={ count } /> ) : <Statistic title="none" value={ 0 } /> }
				</Card>
			</Col>
			<Col xs={ 24 } md={ 6 }>
				<Card title={ <Link to={ listLink( 'events' ) }>Events</Link> }>
					<Statistic title="live now" value={ o.events.live } />
					<Statistic title="kick-offs next 24 h" value={ o.events.next_24h } />
				</Card>
			</Col>
			<Col xs={ 24 } md={ 12 }>
				<Card title={ <Link to={ listLink( 'subscribers' ) }>Signal relay</Link> } extra={ <Typography.Text type="secondary">{ o.signals.last_24h } signals in 24 h</Typography.Text> }>
					<Table dataSource={ o.subscribers } rowKey="id" size="small" pagination={ false }>
						{ [ 'id', 'backlog', 'consecutive_failures', 'last_delivered_at', 'last_error' ].map( ( key ) => (
							<Table.Column key={ key } dataIndex={ key } title={ key.replace( /_/g, ' ' ) } render={ ( value ) => renderValue( key, value, key === 'id' ? 'subscribers' : undefined ) } />
						) ) }
					</Table>
				</Card>
			</Col>
			<Col xs={ 24 } md={ 12 }>
				<Card title={ <Link to={ listLink( 'dual-run-checks' ) }>Dual run</Link> }>
					{ o.dual_run ? (
						<>
							<Typography.Paragraph>Last check { renderValue( 'checked_at', o.dual_run.checked_at ) } ({ renderValue( 'id', o.dual_run.id, 'dual-run-checks' ) })</Typography.Paragraph>
							<Row>
								<Col span={ 6 }><Statistic title="routes" value={ o.dual_run.routes } /></Col>
								<Col span={ 6 }><Statistic title="equal" value={ o.dual_run.equal } /></Col>
								<Col span={ 6 }><Statistic title="explained" value={ o.dual_run.accepted } /></Col>
								<Col span={ 6 }><Statistic title="unexplained" value={ o.dual_run.different + o.dual_run.errors } valueStyle={ o.dual_run.different + o.dual_run.errors ? { color: '#cf1322' } : {} } /></Col>
							</Row>
						</>
					) : (
						<Empty description="No dual-run checks" />
					) }
				</Card>
			</Col>
		</Row>
	);
}
